import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Trash2 } from "lucide-react";
import { createPayPeriod, deletePayPeriod, ensurePayrollRunForPeriod, listPayPeriods } from "./payrollApi";
import DatePickerField from "../../components/common/DatePickerField";
import OrganisationDisplayField from "../../components/common/OrganisationDisplayField";
import { formatPayPeriodLabel } from "../../components/common/PayPeriodSelect";
import { usePayrollWorkflow } from "./payrollWorkflow";
import { shortId } from "./payrollRunUtils";

const PAGE_SIZE = 5;

function formatError(error) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((item) => item?.msg || JSON.stringify(item)).join("; ");
  }
  return error?.message || "Request failed";
}

function formatCreatedAt(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function payrollIdStatus(status) {
  const value = String(status || "open").toLowerCase();
  if (value === "closed") {
    return { label: "Closed", className: "pp-id-badge pp-id-badge--closed" };
  }
  if (value === "locked") {
    return { label: "Locked", className: "pp-id-badge pp-id-badge--locked" };
  }
  return { label: "Created", className: "pp-id-badge pp-id-badge--created" };
}

function payrollIdName(period) {
  return formatPayPeriodLabel(period) || `${period?.start_date || ""} → ${period?.end_date || ""}`;
}

export default function PayPeriods({ embedded = false }) {
  const navigate = useNavigate();
  const { setWorkflow, organisationId, organisationName, payPeriodId } = usePayrollWorkflow();

  const [form, setForm] = useState({
    organisation_id: "",
    start_date: "",
    end_date: "",
  });
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [periods, setPeriods] = useState([]);
  const [loadingPeriods, setLoadingPeriods] = useState(true);
  const [listError, setListError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (organisationId) {
      setForm((current) => ({
        ...current,
        organisation_id: organisationId,
      }));
    }
  }, [organisationId]);

  const loadPeriods = useCallback(async () => {
    setLoadingPeriods(true);
    setListError(null);
    try {
      const { data } = await listPayPeriods();
      setPeriods(Array.isArray(data) ? data : []);
    } catch (e) {
      setPeriods([]);
      setListError(formatError(e));
    } finally {
      setLoadingPeriods(false);
    }
  }, []);

  useEffect(() => {
    loadPeriods();
  }, [loadPeriods]);

  const pageCount = Math.max(1, Math.ceil(periods.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);

  useEffect(() => {
    if (page !== safePage) setPage(safePage);
  }, [page, safePage]);

  const pageRows = useMemo(() => {
    const start = (safePage - 1) * PAGE_SIZE;
    return periods.slice(start, start + PAGE_SIZE);
  }, [periods, safePage]);

  const rangeStart = periods.length === 0 ? 0 : (safePage - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(safePage * PAGE_SIZE, periods.length);

  const openPayrun = (name, runId, period) => {
    setWorkflow({
      organisationId: period.organisation_id || organisationId,
      organisationName,
      payPeriodId: period.pay_period_id,
      payPeriodLabel: name,
      payPeriodStart: period.start_date,
      payPeriodEnd: period.end_date,
      payrollRunId: runId,
      payrollRunLabel: name,
    });
    navigate("/payroll-process?tab=process", {
      state: {
        payrollIdNotice: {
          kind: "created",
          name,
        },
      },
    });
  };

  const submit = async () => {
    setError(null);

    if (!form.organisation_id || !form.start_date || !form.end_date) {
      setError("Organisation, start date, and end date are required.");
      return;
    }

    if (form.end_date < form.start_date) {
      setError("End date must be on or after the start date.");
      return;
    }

    setSubmitting(true);
    try {
      const { data } = await createPayPeriod(form);
      const name = payrollIdName(data);
      const run = await ensurePayrollRunForPeriod({
        organisationId: data.organisation_id || form.organisation_id,
        payPeriodId: data.pay_period_id,
      });
      setForm((current) => ({ ...current, start_date: "", end_date: "" }));
      openPayrun(name, run.payroll_run_id, data);
    } catch (e) {
      setError(formatError(e));
      await loadPeriods();
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (period) => {
    const name = payrollIdName(period);
    if (
      !window.confirm(
        `Delete Payroll ID "${name}"?\n\nDraft pay runs for this Payroll ID will also be removed. This cannot be undone.`
      )
    ) {
      return;
    }

    setDeletingId(period.pay_period_id);
    setListError(null);
    try {
      await deletePayPeriod(period.pay_period_id);
      if (String(payPeriodId) === String(period.pay_period_id)) {
        setWorkflow({
          payPeriodId: "",
          payPeriodLabel: "",
          payPeriodStart: "",
          payPeriodEnd: "",
          payrollRunId: "",
          payrollRunLabel: "",
        });
      }
      await loadPeriods();
    } catch (e) {
      setListError(formatError(e));
    } finally {
      setDeletingId(null);
    }
  };

  const content = (
    <div className="pp-id-layout">
      <section className="pp-id-card">
        <div className="pp-id-card__head">
          <div>
            <h2 className="pp-id-card__title">New Payroll ID</h2>
            <p className="pp-id-card__sub">
              Set the date range. This same Payroll ID name is used on the pay run page.
            </p>
          </div>
        </div>
        <div className="pp-id-card__body">
          <div className="pp-id-form">
            <div className="pp-id-form__org">
              <OrganisationDisplayField
                organisationId={organisationId}
                organisationName={organisationName}
                hint="This Payroll ID is created for your organisation."
              />
            </div>
            <DatePickerField
              label="Start date"
              value={form.start_date}
              onChange={(date) => setForm({ ...form, start_date: date })}
              max={form.end_date || undefined}
            />
            <DatePickerField
              label="End date"
              value={form.end_date}
              onChange={(date) => setForm({ ...form, end_date: date })}
              min={form.start_date || undefined}
            />
          </div>

          {error && (
            <div className="pp-id-alert" role="alert">
              {error}
            </div>
          )}

          <div className="pp-id-form__actions">
            <button
              type="button"
              onClick={submit}
              disabled={submitting}
              className="pp-id-primary"
            >
              {submitting ? "Creating Payroll ID…" : "Create Payroll ID"}
            </button>
          </div>
        </div>
      </section>

      <section className="pp-id-card">
        <div className="pp-id-card__head">
          <div>
            <h2 className="pp-id-card__title">Payroll IDs</h2>
            <p className="pp-id-card__sub">
              {periods.length === 0
                ? "Created Payroll IDs appear here."
                : `Showing ${rangeStart}–${rangeEnd} of ${periods.length}`}
            </p>
          </div>
          <button
            type="button"
            onClick={loadPeriods}
            disabled={loadingPeriods}
            className="pp-id-refresh"
          >
            {loadingPeriods ? "Refreshing…" : "Refresh"}
          </button>
        </div>

        <div className="pp-id-card__body pp-id-card__body--table">
          {listError && (
            <div className="pp-id-alert" role="alert">
              {listError}
            </div>
          )}

          {loadingPeriods ? (
            <p className="pp-id-empty">Loading Payroll IDs…</p>
          ) : periods.length === 0 ? (
            <p className="pp-id-empty">No Payroll ID yet. Create one above to open the pay run.</p>
          ) : (
            <>
              <div className="pp-id-table-wrap">
                <table className="pp-id-table">
                  <thead>
                    <tr>
                      <th>Payroll ID</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th className="pp-id-table__actions">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map((period) => {
                      const status = payrollIdStatus(period.status);
                      const name = payrollIdName(period);
                      const isDeleting = deletingId === period.pay_period_id;
                      return (
                        <tr key={period.pay_period_id}>
                          <td>
                            <div className="pp-id-name">{name}</div>
                            <div className="pp-id-ref" title={period.pay_period_id}>
                              {shortId(period.pay_period_id)}
                            </div>
                          </td>
                          <td>
                            <span className={status.className}>{status.label}</span>
                          </td>
                          <td className="pp-id-created">{formatCreatedAt(period.created_at)}</td>
                          <td className="pp-id-table__actions">
                            <button
                              type="button"
                              onClick={() => handleDelete(period)}
                              disabled={isDeleting || Boolean(deletingId)}
                              className="pp-id-delete"
                              title="Delete Payroll ID"
                            >
                              <Trash2 size={14} />
                              {isDeleting ? "Deleting…" : "Delete"}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {pageCount > 1 && (
                <div className="pp-id-pager">
                  <p className="pp-id-pager__meta">
                    Page {safePage} of {pageCount}
                  </p>
                  <div className="pp-id-pager__controls">
                    <button
                      type="button"
                      className="pp-id-pager__btn"
                      onClick={() => setPage((current) => Math.max(1, current - 1))}
                      disabled={safePage <= 1}
                    >
                      <ChevronLeft size={16} />
                      Previous
                    </button>
                    {Array.from({ length: pageCount }, (_, index) => {
                      const number = index + 1;
                      return (
                        <button
                          key={number}
                          type="button"
                          className={`pp-id-pager__page${number === safePage ? " is-active" : ""}`}
                          onClick={() => setPage(number)}
                          aria-current={number === safePage ? "page" : undefined}
                        >
                          {number}
                        </button>
                      );
                    })}
                    <button
                      type="button"
                      className="pp-id-pager__btn"
                      onClick={() => setPage((current) => Math.min(pageCount, current + 1))}
                      disabled={safePage >= pageCount}
                    >
                      Next
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );

  if (embedded) {
    return <div className="pp-hub-embedded">{content}</div>;
  }

  return (
    <div className="bw-page bg-gray-50 min-h-screen pp-hub">
      <header className="bw-page-header pp-hub-header">
        <div className="pp-hub-header__row relative z-[1]">
          <h1 className="pp-hub-standalone-title">Payroll ID</h1>
        </div>
      </header>
      <div className="pp-hub-body">{content}</div>
    </div>
  );
}
